import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Zap } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export type ImportType = "buyers" | "clients" | "prospects" | "coi" | "contacts";

interface ImportDialogProps {
  type: ImportType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ImportResult {
  success: boolean;
  summary: {
    companiesCreated?: number;
    contactsCreated?: number;
    contactsSkipped?: number;
    errors: number;
  };
  errors: { row: number; message: string }[];
  skipped?: { row: number; sheet?: string; message: string }[];
  warnings?: { row: number; message: string }[];
  createdIds?: string[];
}

interface EnrichResult {
  enriched: number;
  failed: number;
  skipped: number;
  errors: { id: string; name: string; error: string }[];
}

const TYPE_LABELS: Record<ImportType, string> = {
  buyers: "Buyers",
  clients: "Clients",
  prospects: "Prospects",
  coi: "COI Contacts",
  contacts: "Contacts",
};

const INVALIDATION_KEYS: Record<ImportType, string[][]> = {
  buyers: [["buyers"], ["companies"]],
  clients: [["clients"], ["companies"]],
  prospects: [["prospects"], ["companies"], ["/api/prospects"]],
  coi: [["cois"], ["contacts"]],
  contacts: [["contacts"], ["companies"]],
};

export function ImportDialog({ type, open, onOpenChange }: ImportDialogProps) {
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [enrichResult, setEnrichResult] = useState<EnrichResult | null>(null);
  const [enriching, setEnriching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const label = TYPE_LABELS[type];

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`/api/import/${type}`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Import failed");
      }

      return res.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => {
      setResult(data);
      INVALIDATION_KEYS[type].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      );

      const total = (data.summary.companiesCreated ?? 0) + (data.summary.contactsCreated ?? 0);
      const skippedCount = data.summary.contactsSkipped ?? 0;
      const skippedNote = `${skippedCount} already existed and were skipped`;
      if (data.summary.errors === 0) {
        if (skippedCount > 0) {
          toast.success(`${total} created, ${skippedNote}`);
        } else {
          toast.success(`Successfully imported ${total} ${label.toLowerCase()}`);
        }
      } else {
        toast.warning(
          `Import completed with ${data.summary.errors} error${data.summary.errors !== 1 ? "s" : ""}` +
          (skippedCount > 0 ? `, ${skippedNote}` : "")
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = `/api/import/template/${type}`;
    a.download = `import_${type}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Template downloaded");
  };

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large (max 10 MB)");
      return;
    }
    setResult(null);
    setEnrichResult(null);
    importMutation.mutate(file);
  };

  const handleEnrichAll = async () => {
    if (!result?.createdIds?.length) return;
    setEnriching(true);
    try {
      const res = await fetch("/api/prospects/enrich-batch", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: result.createdIds }),
      });
      const data = await res.json() as EnrichResult;
      setEnrichResult(data);
      INVALIDATION_KEYS[type].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      );
      if (data.enriched > 0) {
        toast.success(`Enriched ${data.enriched} prospect${data.enriched !== 1 ? "s" : ""} via Apollo`);
      }
    } catch {
      toast.error("Batch enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleClose = () => {
    setResult(null);
    setEnrichResult(null);
    setEnriching(false);
    importMutation.reset();
    onOpenChange(false);
  };

  const summaryText = () => {
    if (!result) return "";
    const parts: string[] = [];
    if (result.summary.companiesCreated) parts.push(`${result.summary.companiesCreated} companies`);
    if (result.summary.contactsCreated) parts.push(`${result.summary.contactsCreated} contacts`);
    const created = parts.length ? `Created ${parts.join(" and ")}` : "0 records created";
    const skippedCount = result.summary.contactsSkipped ?? 0;
    return skippedCount > 0 ? `${created} · ${skippedCount} already existed and were skipped` : created;
  };

  const canEnrich = type === "prospects" && result?.createdIds && result.createdIds.length > 0 && !enrichResult;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary-text" />
            Import {label}
          </DialogTitle>
          <DialogDescription>
            Download the template, fill it in, then upload to import multiple {label.toLowerCase()} at once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Step 1 */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-sm">Step 1: Download Template</p>
              <p className="text-xs text-muted-foreground mt-1">
                Get the Excel template with the correct columns, example rows, and instructions.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-2 shrink-0">
              <Download className="w-4 h-4" />
              Template
            </Button>
          </div>

          {/* Step 2 */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-sm">Step 2: Fill In Your Data</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add your {label.toLowerCase()} to the sheet. Delete the gray example rows first.
                See the Instructions tab for field details.
              </p>
            </div>
          </div>

          {/* Step 3: Upload */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
              importMutation.isPending && "opacity-50 pointer-events-none"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
            {importMutation.isPending ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-primary-text animate-spin" />
                <p className="font-medium">Importing...</p>
                <p className="text-sm text-muted-foreground">Please wait while we process your file</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-muted-foreground" />
                <div>
                  <p className="font-medium">Step 3: Upload Your File</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Drag and drop your Excel file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Max 10 MB · .xlsx or .xls</p>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div
                className={cn(
                  "flex items-center gap-3 p-4 rounded-lg",
                  result.summary.errors === 0 ? "bg-green-500/10" : "bg-yellow-500/10"
                )}
              >
                {result.summary.errors === 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 dark:text-yellow-400" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {result.summary.errors === 0 ? "Import Successful" : "Import Completed with Errors"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summaryText()}
                    {result.summary.errors > 0 && ` · ${result.summary.errors} row${result.summary.errors !== 1 ? "s" : ""} had errors`}
                  </p>
                </div>
              </div>

              {result.skipped && result.skipped.length > 0 && (
                <div className="max-h-[180px] overflow-y-auto space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Already existed (skipped):</p>
                  {result.skipped.map((s, i) => (
                    <div key={i} className="text-xs p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <span className="font-medium">Row {s.row}:</span> {s.message}
                    </div>
                  ))}
                </div>
              )}

              {result.warnings && result.warnings.length > 0 && (
                <div className="max-h-[180px] overflow-y-auto space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Imported, with warnings:</p>
                  {result.warnings.map((w, i) => (
                    <div key={i} className="text-xs p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <span className="font-medium">Row {w.row}:</span> {w.message}
                    </div>
                  ))}
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="max-h-[180px] overflow-y-auto space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Errors:</p>
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-xs p-2 bg-red-500/10 rounded border border-red-500/20">
                      <span className="font-medium">Row {err.row}:</span> {err.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Enrich (prospects only) */}
          {canEnrich && (
            <div className="flex items-center gap-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <Zap className="w-5 h-5 text-amber-600 shrink-0 dark:text-amber-400" />
              <div className="flex-1">
                <p className="font-medium text-sm">Step 4: Enrich with Apollo</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Fill in company details (industry, revenue, employees, LinkedIn, etc.) for {result!.createdIds!.length} imported prospect{result!.createdIds!.length !== 1 ? "s" : ""} using Apollo.
                </p>
              </div>
              <Button size="sm" onClick={handleEnrichAll} disabled={enriching} className="gap-2 shrink-0">
                {enriching ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Enriching...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Enrich All</>
                )}
              </Button>
            </div>
          )}

          {/* Enrichment Results */}
          {enrichResult && (
            <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 dark:text-green-400" />
              <div className="flex-1">
                <p className="font-medium text-sm">Enrichment Complete</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {enrichResult.enriched} enriched
                  {enrichResult.failed > 0 && ` · ${enrichResult.failed} failed`}
                  {enrichResult.skipped > 0 && ` · ${enrichResult.skipped} skipped (no website)`}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={handleClose}>
            {result ? "Done" : "Cancel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
